import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Paper, Divider, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "../../../config/axiosConfig";
import { CAJA_TECNICA_COLOR } from "../theme";
import { fieldSx as compactFieldSx } from "../../egresos/components/formStyles";

// El legacy (busqueda.php/Guarda_ingreso.php de Técnica) no tiene campo de Observación
// ni edición para Ingresos — mismo caso que el resto de Cajas. Se replica tal cual: solo
// Fecha, N° de Cheque y Monto, sin editar.
const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.74rem",
  padding: "2px 6px",
  lineHeight: 1.2,
  color: theme.palette.mode === "dark" ? theme.palette.common.white : CAJA_TECNICA_COLOR,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : `${CAJA_TECNICA_COLOR}14`,
}));

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const initialForm = { fecha: hoyISO(), num_cheque: "", monto: "" };

const RegistroIngresosTab = () => {
  const [saldo, setSaldo] = useState(null);
  const [ingresos, setIngresos] = useState([]);
  const [totalIngresos, setTotalIngresos] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(10);
  const [loadingLista, setLoadingLista] = useState(true);
  const [errorLista, setErrorLista] = useState("");

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const cargarLista = useCallback((paginaObjetivo = pagina, filasObjetivo = filasPorPagina) => {
    setLoadingLista(true);
    setErrorLista("");
    Promise.all([
      axios.get("/api/caja-tecnica/egresos/saldo"),
      axios.get("/api/caja-tecnica/ingresos", { params: { page: paginaObjetivo + 1, pageSize: filasObjetivo } }),
    ]).then(([resSaldo, resIngresos]) => {
      setSaldo(resSaldo.data?.data?.monto ?? null);
      setIngresos(resIngresos.data?.data?.items || []);
      setTotalIngresos(Number(resIngresos.data?.data?.total || 0));
    }).catch((e) => setErrorLista(e?.response?.data?.message || "Error al cargar los ingresos."))
      .finally(() => setLoadingLista(false));
  }, [pagina, filasPorPagina]);

  useEffect(() => {
    cargarLista(pagina, filasPorPagina);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, filasPorPagina]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const handleClear = () => {
    setForm(initialForm);
    setError("");
  };

  const handleGuardar = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post("/api/caja-tecnica/ingresos", {
        fecha: form.fecha,
        num_cheque: form.num_cheque.trim(),
        monto: Number(form.monto),
      });
      setForm(initialForm);
      if (pagina === 0) cargarLista(0, filasPorPagina);
      else setPagina(0);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al registrar el ingreso.");
    } finally {
      setSaving(false);
    }
  };

  const puedeGuardar = form.fecha && form.num_cheque.trim() && Number(form.monto) > 0 && !saving;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Ingresos Caja Técnica
      </Typography>

      {errorLista && <Alert severity="error" sx={{ mb: 1 }}>{errorLista}</Alert>}

      {loadingLista ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
      ) : ingresos.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>No existen Ingresos.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ mb: 1 }}>
          <TableContainer>
            <Table size="small" sx={{ "& td": { py: 0.15, px: 0.75, fontSize: "0.74rem", lineHeight: 1.2 } }}>
              <TableHead>
                <TableRow>
                  <Th>Cod</Th>
                  <Th align="right">Fecha</Th>
                  <Th>N° de Cheque</Th>
                  <Th align="right">Ingreso Bs.</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {ingresos.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.id}</TableCell>
                    <TableCell align="right">{formatFecha(r.fecha)}</TableCell>
                    <TableCell>{r.num_fac ? `Cheque BNB # ${r.num_fac}` : "-"}</TableCell>
                    <TableCell align="right">{Number(r.monto).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalIngresos}
            page={pagina}
            rowsPerPage={filasPorPagina}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, nuevaPagina) => setPagina(nuevaPagina)}
            onRowsPerPageChange={(e) => { setFilasPorPagina(Number(e.target.value)); setPagina(0); }}
          />
        </Paper>
      )}

      {saldo !== null && (
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, color: saldo < 0 ? "error.main" : CAJA_TECNICA_COLOR }}>
          Saldo {saldo < 0 ? "Negativo " : ""}{saldo.toFixed(2)}
        </Typography>
      )}

      <Divider sx={{ mb: 1.5 }} />

      <Paper variant="outlined" sx={{ p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <Stack spacing={0.6}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 0.6 }}>
            <TextField
              size="small" type="date" label="Fecha de Registro" required
              InputLabelProps={{ shrink: true }}
              value={form.fecha} onChange={handleChange("fecha")}
              sx={compactFieldSx}
            />
            <TextField
              size="small" label="N° de Cheque" required
              value={form.num_cheque} onChange={handleChange("num_cheque")}
              sx={compactFieldSx}
            />
            <TextField
              size="small" type="number" label="Monto" required
              value={form.monto} onChange={handleChange("monto")}
              inputProps={{ min: 0, step: "0.01" }}
              sx={compactFieldSx}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={handleClear} disabled={saving}>Borrar</Button>
            <Button
              variant="contained" onClick={handleGuardar} disabled={!puedeGuardar}
              sx={{ bgcolor: CAJA_TECNICA_COLOR, "&:hover": { bgcolor: "#20292e" } }}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default RegistroIngresosTab;
